import output from "_src/engine/output";

let _parseTaskId = 0;
let _parserStates = new Map();
class ParserState {
  constructor() {
    this.decoder = new TextDecoder();
    this.truncatedLine = "";
    this.currentObjectName = null;
    this.normalsIndicesEnabled = false;
    this.uvsIndicesEnabled = false;
  }
}

// o object_name | g group_name
const _object_pattern = /^[og]\s*(.+)?/;
// mtllib file_reference
const _material_library_pattern = /^mtllib /;
// usemtl material_name
const _material_use_pattern = /^usemtl /;
// usemap map_name
const _map_use_pattern = /^usemap /;
const _face_vertex_data_separator_pattern = /\s+/;

export default {
  startParseTask() {
    const newTaskId = _parseTaskId++;
    _parserStates.set(newTaskId, new ParserState());
    return newTaskId;
  },

  finishParseTask(id) {
    _parserStates.delete(id);
  },

  decode(dataChunk, state, verticesSizes, taskId) {
    const parserState = _parserStates.get(taskId);
    if (!parserState) {
      throw new Error( "ExternalLoader.ObjLoader: task with id " + taskId + " is not initialized");
    }
    let textChunk = parserState.truncatedLine + parserState.decoder.decode(dataChunk, {stream: true});

    if ( textChunk.indexOf( "\r\n" ) !== - 1 ) {
      // This is faster than String.split with regex that splits on both
      textChunk = textChunk.replace( /\r\n/g, "\n" );
    }

    if ( textChunk.indexOf( "\\\n" ) !== - 1 ) {

      // join lines separated by a line continuation character (\)
      textChunk = textChunk.replace( /\\\n/g, "" );

    }

    const lines = textChunk.split( "\n" );
    const lastLine = lines[lines.length - 1];
    const isLastLineFull = lastLine.endsWith("\n");
    const tailDrop = isLastLineFull ? 0 : 1;
    parserState.truncatedLine = isLastLineFull ? "" : lastLine;

    let regexpResult;
    for ( let i = 0, l = lines.length; i < l - tailDrop; i++ ) {
      const line = lines[ i ].trimStart();
      if ( line.length === 0 ) continue;

      const lineFirstChar = line.charAt( 0 );
      // @todo invoke passed in handler if any
      if ( lineFirstChar === "#" ) continue;

      const verticesBuffer = state.vertices;
      const indicesBuffer = state.getIndicesBuffer(parserState.currentObjectName);
      if ( lineFirstChar === "v" ) {

        const data = line.split( _face_vertex_data_separator_pattern );

        switch ( data[ 0 ] ) {

          case "v":
            pushToBuffer(verticesBuffer, verticesBuffer.POSITIONS, data[1]);
            pushToBuffer(verticesBuffer, verticesBuffer.POSITIONS, data[2]);
            pushToBuffer(verticesBuffer, verticesBuffer.POSITIONS, data[3]);
            verticesSizes[verticesBuffer.POSITIONS]++;

            break;
          case "vn":
            pushToBuffer(verticesBuffer, verticesBuffer.NORMALS, data[1]);
            pushToBuffer(verticesBuffer, verticesBuffer.NORMALS, data[2]);
            pushToBuffer(verticesBuffer, verticesBuffer.NORMALS, data[3]);
            verticesSizes[verticesBuffer.NORMALS]++;

            break;
          case "vt":
            pushToBuffer(verticesBuffer, verticesBuffer.UVS, data[1]);
            pushToBuffer(verticesBuffer, verticesBuffer.UVS, data[2]);
            verticesSizes[verticesBuffer.UVS]++;

            break;
        }

      } else if ( lineFirstChar === "f" ) {

        const lineData = line.slice( 1 ).trim();
        const vertexData = lineData.split( _face_vertex_data_separator_pattern );
        const faceVertices = [];

        // Parse the face vertex data into an easy to work with format

        for ( let j = 0, jl = vertexData.length; j < jl; j ++ ) {

          const vertex = vertexData[ j ];

          if ( vertex.length > 0 ) {

            const vertexParts = vertex.split( "/" );
            faceVertices.push( vertexParts );

          }

        }

        // Draw an edge between the first vertex and all subsequent vertices to form an n-gon
        const v1 = faceVertices[ 0 ];

        for ( let j = 1, jl = faceVertices.length - 1; j < jl; j ++ ) {
          const v2 = faceVertices[ j ];
          const v3 = faceVertices[ j + 1 ];
          const v1p = _parseAndFixIndex(v1[0], INDICES_ID, verticesSizes);
          const v2p = _parseAndFixIndex(v2[0], INDICES_ID, verticesSizes);
          const v3p = _parseAndFixIndex(v3[0], INDICES_ID, verticesSizes);
          const v1t = v1[1] ? _parseAndFixIndex(v1[1], UVS_INDICES_ID, verticesSizes) : null;
          const v2t = v2[1] ? _parseAndFixIndex(v2[1], UVS_INDICES_ID, verticesSizes) : null;
          const v3t = v3[1] ? _parseAndFixIndex(v3[1], UVS_INDICES_ID, verticesSizes) : null;
          const v1n = v1[2] ? _parseAndFixIndex(v1[2], NORMALS_INDICES_ID, verticesSizes) : null;
          const v2n = v2[2] ? _parseAndFixIndex(v2[2], NORMALS_INDICES_ID, verticesSizes) : null;
          const v3n = v3[2] ? _parseAndFixIndex(v3[2], NORMALS_INDICES_ID, verticesSizes) : null;

          if (!parserState.uvsIndicesEnabled && (v1t !== null && v2t !== null && v3t !== null) && (v1p !== v1t || v2p !== v2t || v3p !== v3t)) {
            if (indicesBuffer.storage[indicesBuffer.INDICES]) {
              indicesBuffer.storage[indicesBuffer.UVS_INDICES] = indicesBuffer.storage[indicesBuffer.INDICES].slice();
              indicesBuffer.pointers[indicesBuffer.UVS_INDICES] = indicesBuffer.pointers[indicesBuffer.INDICES];
            }
            parserState.uvsIndicesEnabled = true;
          }
          if (!parserState.normalsIndicesEnabled && (v1n !== null && v2n !== null && v3n !== null) && (v1p !== v1n || v2p !== v2n || v3p !== v3n)) {
            if (indicesBuffer.storage[indicesBuffer.INDICES]) {
              indicesBuffer.storage[indicesBuffer.NORMALS_INDICES] = indicesBuffer.storage[indicesBuffer.INDICES].slice();
              indicesBuffer.pointers[indicesBuffer.NORMALS_INDICES] = indicesBuffer.pointers[indicesBuffer.INDICES];
            }
            parserState.normalsIndicesEnabled = true;
          }

          pushToBuffer(indicesBuffer, indicesBuffer.INDICES, v1p);
          pushToBuffer(indicesBuffer, indicesBuffer.INDICES, v2p);
          pushToBuffer(indicesBuffer, indicesBuffer.INDICES, v3p);
          if (parserState.uvsIndicesEnabled) {
            pushToBuffer(indicesBuffer, indicesBuffer.UVS_INDICES, v1t);
            pushToBuffer(indicesBuffer, indicesBuffer.UVS_INDICES, v2t);
            pushToBuffer(indicesBuffer, indicesBuffer.UVS_INDICES, v3t);
          }
          if (parserState.normalsIndicesEnabled) {
            pushToBuffer(indicesBuffer, indicesBuffer.NORMALS_INDICES, v1n);
            pushToBuffer(indicesBuffer, indicesBuffer.NORMALS_INDICES, v2n);
            pushToBuffer(indicesBuffer, indicesBuffer.NORMALS_INDICES, v3n);
          }
        }

      } else if ( lineFirstChar === "l" ) {

        continue;

      } else if ( lineFirstChar === "p" ) {

        continue;

      } else if ( ( regexpResult = _object_pattern.exec( line ) ) !== null ) {

        // o object_name
        // or
        // g group_name

        // WORKAROUND: https://bugs.chromium.org/p/v8/issues/detail?id=2869
        // let name = result[ 0 ].slice( 1 ).trim();
        const name = ( " " + regexpResult[ 0 ].slice( 1 ).trim() ).slice( 1 );
        state.updateUnnamed(name);

        parserState.currentObjectName = name;
        parserState.normalsIndicesEnabled = false;
        parserState.uvsIndicesEnabled = false;

      } else if ( _material_use_pattern.test( line ) ) {

        continue;

      } else if ( _material_library_pattern.test( line ) ) {

        state.materialLibraries.push( line.substring( 7 ).trim() );

      } else if ( _map_use_pattern.test( line ) ) {

        // the line is parsed but ignored since the loader assumes textures are defined MTL files
        // (according to https://www.okino.com/conv/imp_wave.htm, "usemap" is the old-style Wavefront texture reference method)

        output.warn( "ExternalLoader.OBJLoader: Rendering identifier 'usemap' not supported. Textures must be defined in MTL files." );

      } else if ( lineFirstChar === "s" ) {

        continue;

      } else {

        // Handle null terminated files without exception
        if ( line === "\0" ) continue;

        output.warn( "ExternalLoader.OBJLoader: Unexpected line: "  + line);

      }

    }
  },

  shrinkBufferToFit(bufferContainer) {
    for (const bufferId of [POSITIONS_ID, NORMALS_ID, UVS_ID, INDICES_ID, UVS_INDICES_ID, NORMALS_INDICES_ID]) {
      const buffer = bufferContainer.storage[bufferId];
      if (!buffer) continue;

      bufferContainer.storage[bufferId] = bufferContainer.storage[bufferId].subarray(0, bufferContainer.pointers[bufferId]);
    }
  },

  pushToBuffer,
}

function pushToBuffer(bufferContainter, bufferType, data) {
  if (data === undefined) return;
  const bufferSizeLimit = 1850000000 / 4; //1.8gb / 4bytes per float ot uint

  const length = ArrayBuffer.isView(data) ? data.length : 1;
  if (!bufferContainter.storage[bufferType]) {
    bufferContainter.storage[bufferType] = new (_bufferConstructorById(bufferType))(length);
    bufferContainter.pointers[bufferType] = 0;
  }

  if (bufferContainter.storage[bufferType].length < bufferContainter.pointers[bufferType] + length) {
    const oldBuffer = bufferContainter.storage[bufferType];
    const sizeRequest = bufferContainter.pointers[bufferType] + length;
    if (sizeRequest > bufferSizeLimit) {
      throw new Error( "ExternalLoader.ObjLoader: model is too big");
    }
    const newSize = Math.min(bufferSizeLimit, sizeRequest * 2);
    bufferContainter.storage[bufferType] = new (_bufferConstructorById(bufferType))(newSize);
    bufferContainter.storage[bufferType].set(oldBuffer);
  }

  if (ArrayBuffer.isView(data)) {
    bufferContainter.storage[bufferType].set(data, bufferContainter.pointers[bufferType]);
    bufferContainter.pointers[bufferType] += data.length;
  } else {
    bufferContainter.storage[bufferType][bufferContainter.pointers[bufferType]++] = data;
  }
}

function _parseAndFixIndex(stringVal, bufferId, verticesSizes) {
  let bufferLength;
  switch (bufferId) {
    case INDICES_ID:
      bufferLength = verticesSizes[POSITIONS_ID];
      break
    case NORMALS_INDICES_ID:
      bufferLength = verticesSizes[NORMALS_ID];
      break;
    case UVS_INDICES_ID:
      bufferLength = verticesSizes[UVS_ID];
      break;
  }

  let index = parseInt(stringVal, 10);
  if (index < 0) index = bufferLength + index + 1;

  index--;
  return index;
}

const POSITIONS_ID = 0;
const NORMALS_ID = 1;
const UVS_ID = 2;
const INDICES_ID = 3;
const UVS_INDICES_ID = 4;
const NORMALS_INDICES_ID = 5;
class ReallocatingArrayBuffer {
  constructor() {
    this.POSITIONS = POSITIONS_ID;
    this.NORMALS = NORMALS_ID;
    this.UVS = UVS_ID;
    this.INDICES = INDICES_ID;
    this.UVS_INDICES = UVS_INDICES_ID;
    this.NORMALS_INDICES = NORMALS_INDICES_ID;

    this.storage = [];
    this.pointers = [];
  }
}

function _bufferConstructorById(id) {
  switch (id) {
    case POSITIONS_ID:
    case NORMALS_ID:
    case UVS_ID:
      return Float32Array;
    case INDICES_ID:
    case NORMALS_INDICES_ID:
    case UVS_INDICES_ID:
      return Uint32Array;
    default:
      return null;
  }
}

export class State {
  constructor() {
    this.vertices = new ReallocatingArrayBuffer();
    this.objects = {};
    this.unnamedObject = null;
    this.materialLibraries = [];
  }

  getVerticesSizes() {
    const sizes = [];
    sizes[POSITIONS_ID] = this.vertices.pointers[POSITIONS_ID] ? this.vertices.pointers[POSITIONS_ID] / 3 : 0;
    sizes[NORMALS_ID] = this.vertices.pointers[NORMALS_ID] ? this.vertices.pointers[NORMALS_ID] / 3 : 0;
    sizes[UVS_ID] = this.vertices.pointers[UVS_ID] ? this.vertices.pointers[UVS_ID] / 2 : 0;

    return sizes;
  }

  getIndicesBuffer(objectName) {
    if (objectName) {
      if (!this.objects[objectName]) {
        this.objects[objectName] = new ReallocatingArrayBuffer();
      }

      return this.objects[objectName];
    } else {
      if (!this.unnamedObject) {
        this.unnamedObject = new ReallocatingArrayBuffer();
      }

      return this.unnamedObject;
    }
  }

  updateUnnamed(name) {
    if (this.unnamedObject) {
      this.objects[name] = this.unnamedObject;
      this.unnamedObject = null;
    }
  }
}
